import Joi from "joi";

import { phoneJoiString as phonePattern } from "./phone-validation";

// Common validation patterns
const namePattern = Joi.string().trim().min(2).max(100);

// Phone field that accepts both 'phone' and 'phoneNumber' aliases
const phoneFields = {
  phone: phonePattern,
  phoneNumber: phonePattern,
};

// Individual address fields (flat structure)
const addressFields = {
  address: Joi.string().trim().max(200).allow(""), // street address
  city: Joi.string().trim().max(100).allow("").optional(), // Make city explicitly optional and allow empty strings
  state: Joi.string().trim().max(100).allow(""),
  country: Joi.string().trim().max(200).allow(""), // Increased to handle emojis and extra text
  postalCode: Joi.string().trim().max(20).allow(""),
};

// Personal profile validation (for school users)
export const personalProfileValidation = Joi.object({
  fullName: namePattern.required().messages({
    "any.required": "Full name is required",
    "string.min": "Full name must be at least 2 characters",
    "string.max": "Full name cannot exceed 100 characters",
  }),
  dateOfBirth: Joi.date().max("now").optional().messages({
    "date.max": "Date of birth cannot be in the future",
  }),
  gender: Joi.string()
    .valid("male", "female", "other", "prefer_not_to_say")
    .required()
    .messages({
      "any.required": "Gender is required",
      "any.only":
        "Gender must be one of: male, female, other, prefer_not_to_say",
    }),
  age: Joi.number().integer().min(18).max(100).optional().messages({
    "number.min": "Age must be at least 18",
    "number.max": "Age cannot exceed 100",
    "number.integer": "Age must be a whole number",
  }),
  ...phoneFields,
  ...addressFields,
  bio: Joi.string().trim().max(500).optional().messages({
    "string.max": "Bio cannot exceed 500 characters",
  }),
});

// School profile validation
export const schoolProfileValidation = Joi.object({
  schoolName: Joi.string().trim().min(2).max(200).required().messages({
    "any.required": "School name is required",
    "string.min": "School name must be at least 2 characters",
    "string.max": "School name cannot exceed 200 characters",
  }),
  establishmentYear: Joi.number()
    .integer()
    .min(1800)
    .max(new Date().getFullYear())
    .messages({
      "number.min": "Establishment year must be after 1800",
      "number.max": "Establishment year cannot be in the future",
    }),
  description: Joi.string().trim().max(1000).messages({
    "string.max": "Description cannot exceed 1000 characters",
  }),
  ...phoneFields,
  website: Joi.string().trim().uri().messages({
    "string.uri": "Please provide a valid website URL",
  }),
  email: Joi.string().trim().email().lowercase().messages({
    "string.email": "Please provide a valid email address",
  }),
  // Support both nested address object and flat fields
  address: Joi.alternatives()
    .try(
      Joi.string().trim().max(200).allow(""), // Flat address (street)
      Joi.object({
        // Nested address object
        street: Joi.string().trim().max(200).allow(""),
        city: Joi.string().trim().max(100).allow(""),
        state: Joi.string().trim().max(100).allow(""),
        country: Joi.string().trim().max(200).allow(""),
        postalCode: Joi.string().trim().max(20).allow(""),
        coordinates: Joi.array().items(Joi.number()).length(2).optional(),
      }),
    )
    .optional(),
  // Flat address fields (for backward compatibility)
  city: Joi.string().trim().max(100).allow("").optional(),
  state: Joi.string().trim().max(100).allow(""),
  country: Joi.string().trim().max(200).allow(""),
  postalCode: Joi.string().trim().max(20).allow(""),
  // Primary contact information
  primaryContact: Joi.object({
    fullName: Joi.string().trim().min(2).max(100).optional(),
    gender: Joi.string().valid("male", "female", "other").optional(),
    dateOfBirth: Joi.date().max("now").optional().messages({
      "date.max": "Date of birth cannot be in the future",
    }),
    phone: phonePattern.optional(),
    email: Joi.string().trim().email().lowercase().optional(),
    designation: Joi.string().trim().min(2).max(50).optional(),
  }).optional(),
});

// Student profile validation
export const studentProfileValidation = Joi.object({
  fullName: namePattern.required().messages({
    "any.required": "Full name is required",
    "string.min": "Full name must be at least 2 characters",
    "string.max": "Full name cannot exceed 100 characters",
  }),
  dateOfBirth: Joi.date().max("now").optional().messages({
    "date.max": "Date of birth cannot be in the future",
  }),
  age: Joi.number().integer().min(13).max(100).optional().messages({
    "number.min": "Age must be at least 13",
    "number.max": "Age cannot exceed 100",
    "number.integer": "Age must be a whole number",
  }),
  gender: Joi.string()
    .valid("male", "female", "other", "prefer_not_to_say")
    .messages({
      "any.only":
        "Gender must be one of: male, female, other, prefer_not_to_say",
    }),
  ...phoneFields,
  ...addressFields,
  // School ID is required
  schoolId: Joi.string()
    .trim()
    .pattern(/^[0-9a-fA-F]{24}$/)
    .required()
    .messages({
      "string.pattern.base": "School ID must be a valid MongoDB ObjectId",
      "any.required": "School ID is required",
    }),
  academicLevel: Joi.string()
    .valid("freshman", "sophomore", "junior", "senior", "graduate")
    .messages({
      "any.only":
        "Academic level must be one of: freshman, sophomore, junior, senior, graduate",
    }),
  academicYear: Joi.string().trim().max(20),
  majorSubjectsIds: Joi.array()
    .items(
      Joi.string()
        .trim()
        .pattern(/^[0-9a-fA-F]{24}$/),
    )
    .optional()
    .messages({
      "string.pattern.base":
        "Each major subject ID must be a valid MongoDB ObjectId",
    }),
  majorCourseIds: Joi.array()
    .items(
      Joi.string()
        .trim()
        .pattern(/^[0-9a-fA-F]{24}$/),
    )
    .optional()
    .messages({
      "string.pattern.base":
        "Each major course ID must be a valid MongoDB ObjectId",
    }),
  minorSubjects: Joi.array().items(Joi.string().trim().max(100)),
  guardians: Joi.array().items(
    Joi.object({
      name: Joi.string().trim().min(2).max(100).required(),
      relationship: Joi.string().trim().max(50).required(),
      phone: phonePattern.required(),
      email: Joi.string().trim().email().lowercase(),
      address: Joi.string().trim().max(500),
      isPrimary: Joi.boolean().default(false),
      canPickup: Joi.boolean().default(true),
    }),
  ),
  documentTypes: Joi.array()
    .items(
      Joi.string().valid(
        "transcript",
        "medical",
        "identity",
        "guardian_info",
        "other",
      ),
    )
    .messages({
      "any.only":
        "Document type must be one of: transcript, medical, identity, guardian_info, other",
    }),
});

// Teacher profile validation
export const teacherProfileValidation = Joi.object({
  fullName: namePattern.required().messages({
    "any.required": "Full name is required",
    "string.min": "Full name must be at least 2 characters",
    "string.max": "Full name cannot exceed 100 characters",
  }),
  dateOfBirth: Joi.date().max("now").optional().messages({
    "date.max": "Date of birth cannot be in the future",
  }),
  // Support both age and dateOfBirth fields
  age: Joi.number().integer().min(18).max(100).optional().messages({
    "number.min": "Age must be at least 18",
    "number.max": "Age cannot exceed 100",
    "number.integer": "Age must be a whole number",
  }),
  gender: Joi.string()
    .valid("male", "female", "other", "prefer_not_to_say")
    .messages({
      "any.only":
        "Gender must be one of: male, female, other, prefer_not_to_say",
    }),
  ...phoneFields,
  ...addressFields,
  // School ID is required
  schoolId: Joi.string()
    .trim()
    .pattern(/^[0-9a-fA-F]{24}$/)
    .required()
    .messages({
      "string.pattern.base": "School ID must be a valid MongoDB ObjectId",
      "any.required": "School ID is required",
    }),
  bio: Joi.string().trim().max(500).optional().messages({
    "string.max": "Bio cannot exceed 500 characters",
  }),
  // Support both subjects and majorSubjects for compatibility (deprecated)
  subjects: Joi.array()
    .items(
      Joi.object({
        name: Joi.string().trim().max(100).required(),
        qualificationLevel: Joi.string().trim().max(100).allow("").optional(),
        yearsExperience: Joi.number().integer().min(0).max(50).optional(),
        certifications: Joi.array()
          .items(Joi.string().trim().max(200))
          .optional(),
      }),
    )
    .optional(),
  majorSubjects: Joi.array().items(Joi.string().trim().max(100)).optional(),
  courseIds: Joi.array()
    .items(
      Joi.string()
        .trim()
        .pattern(/^[0-9a-fA-F]{24}$/),
    )
    .optional()
    .messages({
      "string.pattern.base": "Each course ID must be a valid MongoDB ObjectId",
    }),
  education: Joi.array()
    .items(
      Joi.object({
        degree: Joi.string().trim().max(100).required(),
        field: Joi.string().trim().max(100).required(),
        institution: Joi.string().trim().max(200).required(),
        graduationYear: Joi.number()
          .integer()
          .min(1950)
          .max(new Date().getFullYear())
          .required(),
        gpa: Joi.number().min(0).max(4).optional(),
      }),
    )
    .optional(),
  experience: Joi.array()
    .items(
      Joi.object({
        position: Joi.string().trim().max(100).required(),
        institution: Joi.string().trim().max(200).required(),
        startDate: Joi.date().required(),
        endDate: Joi.date().min(Joi.ref("startDate")).optional(),
        responsibilities: Joi.string().trim().max(1000).optional(),
        isCurrent: Joi.boolean().default(false).optional(),
      }),
    )
    .optional(),
  emergencyContact: Joi.object({
    name: Joi.string().trim().max(100).optional(),
    relationship: Joi.string().trim().max(50).optional(),
    phone: phonePattern.optional(),
    email: Joi.string().trim().email().lowercase().optional(),
  }).optional(),
  documentTypes: Joi.array()
    .items(
      Joi.string().valid(
        "degree",
        "certification",
        "experience",
        "identity",
        "other",
      ),
    )
    .messages({
      "any.only":
        "Document type must be one of: degree, certification, experience, identity, other",
    }),
});

// Update validation schemas (all fields optional)
export const updatePersonalProfileValidation = personalProfileValidation.fork(
  Object.keys(personalProfileValidation.describe().keys),
  schema => schema.optional(),
);

export const updateSchoolProfileValidation = schoolProfileValidation.fork(
  Object.keys(schoolProfileValidation.describe().keys),
  schema => schema.optional(),
);
